// Small shared pieces of the ticket window (MessageWindow and its side
// views): status tone, short French dates, file glyphs. Pure, no fetching.
import React from 'react';
import { File, FileArchive, FileCode, FileImage, FileText } from 'lucide-react';
import { getStatusClass } from '../constants/ticketStatuses';
import { formatShortDateTime as sharedShortDateTime } from '../utils/dates';

// Ticket status → badge tone (kit: violet = new / automated, blue = in
// progress, orange = waits for someone, green = resolved; an unknown status
// stays neutral). processing_error is the only blocking one.
const STATUS_TONES = {
    'status-new': 'accent',
    'status-open': 'info',
    'status-waiting': 'warning',
    'status-processing': 'info',
    'status-resolved': 'success',
    'status-automated': 'accent',
    'status-pending': 'warning',
};

export const ticketStatusTone = (status) => (status === 'processing_error' ? 'danger' : STATUS_TONES[getStatusClass(status)]);

// Dates: the shared short format (utils/dates — parseUtc, Paris time, KI-110).
// « 28 oct., 10:02 »; the year only when it is not this year.
export const formatShortDateTime = (value) => sharedShortDateTime(value, undefined, { empty: '' });

export const fileKind = (mimeType = '') => {
    if (mimeType.includes('pdf')) return 'pdf';
    if (mimeType.includes('image')) return 'image';
    return 'document';
};

export const FileGlyph = ({ mimeType = '' }) => {
    if (mimeType.includes('pdf')) return <FileText strokeWidth={1.8} aria-hidden="true" />;
    if (mimeType.includes('image')) return <FileImage strokeWidth={1.8} aria-hidden="true" />;
    if (mimeType.includes('zip') || mimeType.includes('rar')) return <FileArchive strokeWidth={1.8} aria-hidden="true" />;
    if (mimeType.includes('code') || mimeType.includes('json')) return <FileCode strokeWidth={1.8} aria-hidden="true" />;
    return <File strokeWidth={1.8} aria-hidden="true" />;
};

// Money as the French write it: « 89,90 € ». An unknown currency code stays
// after the number; a value that is not a number is shown as is.
export const formatMoney = (value, currency) => {
    const number = typeof value === 'number' ? value : Number(value);
    if (value === null || value === undefined || value === '' || Number.isNaN(number)) return value ?? '';
    if (currency && /^[A-Z]{3}$/.test(currency)) {
        try {
            return number.toLocaleString('fr-FR', { style: 'currency', currency });
        } catch (e) {
            return `${number.toLocaleString('fr-FR')} ${currency}`;
        }
    }
    return `${number.toLocaleString('fr-FR')}${currency ? ` ${currency}` : ''}`;
};
